/**
 * list item view
 */
import type { HoursOfOperation } from '@m/types/api/Site';
import type { LicensePlate } from '@m/types/api/Vehicle';
import type { GrpcCurrency } from '@m/types/grpc/GrpcTypes';
import type { IdNameObject } from '@m/types/index';

export enum ViolationTypeName {
  Vision = 'Vision',
  NoVisitStarted = 'No_Visit_Started',
  ExpiredTime = 'Expired_Time',
}

export interface ViolationType {
  description: string;
  id: number;
  name: ViolationTypeName;
}

export interface ViolationItem {
  /** The base issued fee, excluding any applied `lateNoticeFee` (EN-13931, site#38225). Omitted
   * or null when the response only includes the combined `fineAmount`. */
  baseNoticeFee?: number | null;
  /** Base processing fee before any `lateProcessingFee`. The notice modal still shows the
   * combined `processingFeeAmount`. */
  baseProcessingFee?: number | null;
  extId: string;
  /** dollar format 50.5 == $50.50. Combined violation fine; used when `baseNoticeFee` is absent. */
  fineAmount: number;

  id: number;
  /** Escalation increase applied on top of `baseNoticeFee`. `null` or omitted when no schedule
   * exists. */
  lateNoticeFee?: number | null;
  /** Escalation increase applied on top of `baseProcessingFee`. */
  lateProcessingFee?: number | null;
  licensePlate: LicensePlate;
  parkingAmount: number;
  processingFeeAmount: number;

  siteName: string;

  totalAmount: number;

  /** timestamp */
  violationIssued: number;

  violationStatus: IdNameObject;
}

export type ViolationEventDetailsView = {
  eventTime: number;
  licensePlateImageContext: string;
  licensePlateImagePlate: string;
};

export type ViolationRefundStatus = 'Pending' | 'Succeeded' | 'Failed' | 'Rejected';

export type ViolationRefundHistoryItem = {
  addedAt: number;
  amount: GrpcCurrency;
  refundId: number;
  routeId: string;
  status: ViolationRefundStatus;
  updatedAt: number;
  vendorRefundId: string | null;
};

export type ViolationRefundInfo = {
  alreadyRefunded: GrpcCurrency;
  history: ViolationRefundHistoryItem[];
  paymentId: number;
  paymentViolationCount: number;
  refundEligible: boolean;
  remainingRefundable: GrpcCurrency;
  sharedPayment: boolean;
  total: GrpcCurrency;
};

export interface ViolationDetails extends ViolationItem {
  availableActions: IdNameObject[]; // todo: get specific actions
  datePaid: number | null;
  editableFields: IdNameObject[];
  entryView: ViolationEventDetailsView;
  /** The tier snapshot this violation was issued under, `null` unless it actually escalates
   * (more than one tier). Never re-derive amounts/timing from this client-side - it makes no
   * claim about which tier is currently in effect, only what the snapshot itself states. */
  escalationSchedule?: ViolationEscalationSchedule | null;
  exitView: ViolationEventDetailsView;
  /** From the violation_context persisted at issuance, never recomputed. `null`/omitted for a
   * violation issued before this was recorded - distinct from `false`. */
  isFirstViolation?: boolean | null;
  isMember?: boolean | null;

  originalFineAmount?: number | null;
  refund?: ViolationRefundInfo | null;
  sentNotice1: number | null;
  sentNotice2: number | null;

  site: {
    city: string;
    hoursOfOperation: HoursOfOperation;
    id: number;
    internalName: string;
    latitude: number;
    longitude: number;
    name: string;
    state: string;
    street: string;
    zip: string;
  };

  siteTimeZone: string | null;

  skiptrace: Skiptrace | null;

  violationType: ViolationType;
}

export interface Skiptrace {
  address: string;
  city: string;
  // all fields can be empty string
  firstName: string;
  lastName: string;
  state: string;
  zipcode: string;
}

export interface ViolationPlan {
  effectiveDate: string; // DATE_FORMAT_ISO_8601 yyyy-mm-dd
  fineAmount: number;
  id: number;
  isPaused: boolean;
}

/** One step of a violation's own escalation schedule (EN-13931), as opposed to a site's live
 * `ViolationPlan` config, which changes over time independently of any already-issued notice. */
export interface ViolationEscalationTier {
  /** timestamp; the exact instant this tier's amounts apply. Deliberately not a day offset -
   * see EN-13931's `ViolationEscalationTierView` for why the backend won't ship one. */
  effectiveDate: number;
  fee: number;
  fine: number;
}

export interface ViolationEscalationSchedule {
  tiers: ViolationEscalationTier[];
}
